import {
  Alert,
  Box,
  Card,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'

import { ContentWrapper } from '../../../../components/ContentWrapper'
import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import {
  DATE_FORMAT_INTERNATIONAL,
  ONLY_DATE_FORMAT,
} from '../../../../constants/global'
import PageUrls from '../../../../constants/page-urls'
import { EPLATFORM } from '../../../../constants/platform'
import useAxios from '../../../../hooks/useAxios'
import { ITikTokAccount } from '../../../../types/tiktok'
import StockDeductionTab from '../../components/StockDeduction/StockDeductionTab'
import BackfillButton from './components/BackfillButton'
import ByStatusTable from './components/ByStatusTable'
import CostDetailModal from './components/CostDetailModal'
import DailyTable from './components/DailyTable'
import FinanceCards from './components/FinanceCards'
import OrdersModal from './components/OrdersModal'
import ReportFilter from './components/ReportFilter'
import SaleOrdersTab from './components/SaleOrdersTab'
import SettlementBreakdownTable from './components/SettlementBreakdownTable'
import SettlementCards from './components/SettlementCards'
import StockBackfillPanel from './components/StockBackfillPanel'
import SummaryCards from './components/SummaryCards'
import { useTiktokCosts } from './hooks/useTiktokCosts'
import {
  EMPTY_DAILY_ROW,
  IDailyDisplayRow,
  IDailyReport,
  IOrderReport,
  IReportFilter,
} from './types'
import { calcEstimatedProfit, calcSettledProfit } from './utils'

const defaultFilter: IReportFilter = {
  startDate: dayjs().subtract(30, 'day').format(DATE_FORMAT_INTERNATIONAL),
  endDate: dayjs().format(DATE_FORMAT_INTERNATIONAL),
}

const TiktokSalesReportPage = () => {
  const [filter, setFilter] = useState<IReportFilter>(defaultFilter)
  const [searchFilter, setSearchFilter] = useState<IReportFilter>(defaultFilter)
  // เปลี่ยนค่า → รายงานตัดสต๊อกโหลดใหม่หลังตัดย้อนหลังเสร็จ
  const [stockReloadKey, setStockReloadKey] = useState<number>(0)
  const reloadStockReport = useCallback(
    () => setStockReloadKey((prev) => prev + 1),
    [],
  )
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [activeTab, setActiveTab] = useState<number>(0)
  // มุมมองรายงาน tab สถานะ: order_date (default) หรือ settlement_date (รอบจ่าย) — apply ทันที
  const [reportBy, setReportBy] = useState<'order' | 'settlement'>('order')
  // modal list คำสั่งซื้อ — ใช้ร่วมทั้ง tab สถานะ (กดแถวสถานะ) และ tab รายวัน (กดปุ่มดูออเดอร์)
  const [ordersModal, setOrdersModal] = useState<{
    open: boolean
    status?: string
    startDate?: string
    endDate?: string
    by?: 'order' | 'settlement'
    paymentType?: 'cod' | 'transfer'
    title?: string
  }>({ open: false })

  // กดแถวสถานะ → list ตามสถานะ + ช่วงวันที่ค้นหา + มุมมองปัจจุบัน
  const handleSelectStatus = (status?: string) => {
    setOrdersModal({
      open: true,
      status,
      startDate: searchFilter.startDate,
      endDate: searchFilter.endDate,
      by: reportBy,
    })
  }

  // กดปุ่มในตารางรายวัน → list ออเดอร์เฉพาะวันนั้น (ทุกสถานะ, ตามวันสั่ง)
  const handleSelectDate = (date: string) => {
    setOrdersModal({
      open: true,
      startDate: date,
      endDate: date,
      by: 'order',
      title: `คำสั่งซื้อ — ${dayjs(date).format(ONLY_DATE_FORMAT)}`,
    })
  }

  // กด icon ในคอลัมน์ COD/โอน → list ออเดอร์วันนั้น เฉพาะประเภทชำระเงินนั้น
  const handleSelectPayment = (
    date: string,
    paymentType: 'cod' | 'transfer',
  ) => {
    setOrdersModal({
      open: true,
      startDate: date,
      endDate: date,
      by: 'order',
      paymentType,
      title: `คำสั่งซื้อ (${paymentType === 'cod' ? 'COD' : 'โอน'}) — ${dayjs(
        date,
      ).format(ONLY_DATE_FORMAT)}`,
    })
  }

  // modal รายการค่าใช้จ่ายของวันนั้น (กด icon ในคอลัมน์ค่าใช้จ่าย)
  const [costModal, setCostModal] = useState<{ open: boolean; date?: string }>({
    open: false,
  })

  const handleSelectCostDate = (date: string) => {
    setCostModal({ open: true, date })
  }

  const { data: tiktokAccounts } = useAxios<ITikTokAccount[]>({
    method: 'GET',
    url: '/tiktok/merchant/accounts',
  })

  const shopNames = useMemo(
    () =>
      Object.fromEntries(
        (tiktokAccounts ?? []).map((account) => [
          account.shopId,
          account.sellerName,
        ]),
      ),
    [tiktokAccounts],
  )

  const onFilterChange = useCallback((newFilter: IReportFilter) => {
    setFilter(newFilter)
  }, [])

  const handleSearch = (newFilter: IReportFilter) => {
    setFilter(newFilter)
    setSearchFilter(newFilter)
  }

  const params: IReportFilter = {
    startDate: searchFilter.startDate,
    endDate: searchFilter.endDate,
    ...(searchFilter.shopId ? { shopId: searchFilter.shopId } : {}),
  }

  // สถานะ (tab 1) — by = มุมมองวันสั่ง/รอบจ่าย (เปลี่ยนแล้ว refetch เพราะ params เปลี่ยน)
  const report = useAxios<IOrderReport>({
    method: 'GET',
    url: '/tiktok/report/orders',
    params: { ...params, by: reportBy },
  })

  // รายวัน (tab 2) — API ใหม่
  const daily = useAxios<IDailyReport>({
    method: 'GET',
    url: '/tiktok/report/orders-daily',
    params,
  })

  // ค่าใช้จ่าย TikTok (กรอง type=tiktok / ref=shopId ฝั่ง client)
  const {
    costTotal,
    costByDate,
    isLoading: costLoading,
  } = useTiktokCosts(searchFilter)

  // ---- Tab 1: แยกตามสถานะ ----
  const statusOptions = useMemo(
    () =>
      (report.data?.byStatus ?? [])
        .map((bucket) => bucket.status)
        .filter((status): status is string => !!status),
    [report.data],
  )

  const displayedSummary = useMemo(
    () =>
      statusFilter
        ? report.data?.byStatus?.find(
            (bucket) => bucket.status === statusFilter,
          )
        : report.data?.summary,
    [statusFilter, report.data],
  )

  const displayedRows = useMemo(() => {
    const rows = report.data?.byStatus ?? []
    return statusFilter
      ? rows.filter((bucket) => bucket.status === statusFilter)
      : rows
  }, [statusFilter, report.data])

  // ยอดขายสำเร็จ (COMPLETED + DELIVERED) — BE คำนวณมาให้ใน summary แล้ว
  const successTotal = report.data?.summary?.successTotal ?? 0

  // ---- Tab 2: รายวัน (รวมยอดขาย + ค่าใช้จ่ายต่อวัน) ----
  const dailyRows = useMemo<IDailyDisplayRow[]>(() => {
    const salesRows = daily.data?.records ?? []
    const salesByDate: Record<string, (typeof salesRows)[number]> = {}
    salesRows.forEach((row) => {
      salesByDate[row.date] = row
    })

    const allDates = new Set<string>([
      ...salesRows.map((row) => row.date),
      ...Object.keys(costByDate),
    ])

    return Array.from(allDates)
      .sort()
      .map((date) => {
        // spread ...sales เพื่อให้ breakdown ต่อวันไหลเข้า SettlementBreakdownTable (expand row)
        const sales = salesByDate[date] ?? EMPTY_DAILY_ROW
        const cost = costByDate[date] ?? 0
        return {
          ...sales,
          date,
          cost,
          estimatedProfit: calcEstimatedProfit(sales, cost),
          settledProfit: calcSettledProfit(sales, cost),
        }
      })
  }, [daily.data, costByDate])

  return (
    <ContentWrapper>
      <Helmet title="รายงานยอดขาย TikTok" />
      <HeaderContent
        goBack
        title="รายงานยอดขาย TikTok"
        descriptions="รายงานยอดขายจาก TikTok"
        childrenRight={<BackfillButton />}
      />
      <Card sx={{ padding: 2 }}>
        <ReportFilter
          tiktokAccounts={tiktokAccounts ?? []}
          filter={filter}
          statusFilter={statusFilter}
          statusOptions={statusOptions}
          showStatus={activeTab === 0}
          onFilterChange={onFilterChange}
          onStatusChange={setStatusFilter}
          onSearch={handleSearch}
        />

        <Tabs
          value={activeTab}
          onChange={(_e, value) => setActiveTab(value)}
          sx={{ mb: 2 }}
        >
          <Tab label="รายงานยอดขายแยกตามสถานะ" />
          <Tab label="รายงานยอดขายรายวัน" />
          <Tab label="รายการคำสั่งซื้อ" />
          <Tab label="รายงานตัดสต๊อก" />
        </Tabs>

        {report.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            ไม่สามารถโหลดรายงานได้ กรุณาลองใหม่อีกครั้ง
          </Alert>
        )}

        {(report.data?.summary?.costMissingCount ?? 0) > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            ต้นทุนสินค้าไม่ครบ {report.data?.summary?.costMissingCount} รายการ —
            บางสินค้ายังไม่ได้ผูก SKU ของ TikTok กับสินค้าในระบบ
            ยอดต้นทุน/กำไรขั้นต้นจึงต่ำกว่าความเป็นจริง
          </Alert>
        )}

        <Box hidden={activeTab !== 0}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
              flexWrap: 'wrap',
            }}
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              color="primary"
              value={reportBy}
              onChange={(_e, value) => value && setReportBy(value)}
            >
              <ToggleButton value="order">ตามวันที่สั่ง</ToggleButton>
              <ToggleButton value="settlement">ตามรอบจ่าย</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              {reportBy === 'settlement'
                ? 'แสดงออเดอร์ตามรอบที่ TikTok จ่ายเงิน (settlement) ในช่วงวันที่เลือก'
                : 'แสดงออเดอร์ตามวันที่สั่งซื้อ — ยอดที่ได้รับจริงนับเฉพาะออเดอร์ที่จ่ายแล้ว'}
            </Typography>
          </Box>
          <FinanceCards
            successTotal={successTotal}
            successCostTotal={report.data?.summary?.successCostTotal ?? 0}
            feeTotal={report.data?.summary?.feeTotal ?? 0}
            netTotal={report.data?.summary?.netTotal ?? 0}
            costTotal={costTotal}
          />
          <SummaryCards summary={displayedSummary} />
          <SettlementCards summary={displayedSummary} />
          <SettlementBreakdownTable summary={displayedSummary} />
          <ByStatusTable
            data={displayedRows}
            isLoading={report.isLoading}
            onSelectStatus={handleSelectStatus}
          />
        </Box>

        <Box hidden={activeTab !== 1}>
          <DailyTable
            data={dailyRows}
            isLoading={daily.isLoading || costLoading}
            onSelectDate={handleSelectDate}
            onSelectCostDate={handleSelectCostDate}
            onSelectPayment={handleSelectPayment}
          />
        </Box>

        {/* mount เมื่อเปิดแท็บเท่านั้น — จะได้ไม่ยิง list ทิ้งทุกครั้งที่เข้าหน้า */}
        {activeTab === 2 && (
          <SaleOrdersTab
            filter={searchFilter}
            tiktokAccounts={tiktokAccounts ?? []}
          />
        )}

        {/* mount เมื่อเปิดแท็บเท่านั้น — จะได้ไม่ยิงรายงานตัดสต๊อกทิ้งทุกครั้งที่เข้าหน้า */}
        {activeTab === 3 && (
          <StockDeductionTab
            platform={EPLATFORM.TIKTOK}
            filter={searchFilter}
            detailUrl={PageUrls.Platforms.TiktokStockReportDetail}
            reloadKey={stockReloadKey}
            extra={
              <StockBackfillPanel
                shopId={searchFilter.shopId}
                shopName={
                  searchFilter.shopId
                    ? shopNames[searchFilter.shopId]
                    : undefined
                }
                shopNames={shopNames}
                onCompleted={reloadStockReport}
              />
            }
          />
        )}
      </Card>

      <OrdersModal
        open={ordersModal.open}
        status={ordersModal.status}
        startDate={ordersModal.startDate}
        endDate={ordersModal.endDate}
        shopId={searchFilter.shopId}
        by={ordersModal.by}
        paymentType={ordersModal.paymentType}
        title={ordersModal.title}
        onClose={() => setOrdersModal((prev) => ({ ...prev, open: false }))}
      />

      <CostDetailModal
        open={costModal.open}
        date={costModal.date}
        shopId={searchFilter.shopId}
        onClose={() => setCostModal((prev) => ({ ...prev, open: false }))}
      />
    </ContentWrapper>
  )
}

export default TiktokSalesReportPage
